import { ChevronRightIcon } from '@heroicons/react/16/solid';

import { BreadcrumbItem } from './BreadcrumbItem';

import { MobileBreadCrumb } from './MobileBreadCrumb';

import { useTranslation } from 'react-i18next';

export interface IBreadcrumbItem {
  name: string;
  to?: string;
  isAnchor?: boolean;
  icon?: React.ComponentType<{ className: string }>;
  /**
   * Renders in place of `name` for a crumb that is more than text, such as an
   * editable title. `name` is still required, as the key and the fallback.
   */
  content?: React.ReactNode;
}

export interface BreadcrumbsProps {
  items: IBreadcrumbItem[];
}

const Separator = () => (
  <ChevronRightIcon
    className="size-3.5 shrink-0 text-gray-300 dark:text-gray-600"
    aria-hidden="true"
  />
);

/**
 * Every crumb but the last links to its page; the last is the current page.
 * Below `md`, the crumbs between the first and the last collapse into a menu.
 */
export function Breadcrumbs({ items }: BreadcrumbsProps) {
  const { t } = useTranslation('designSystem');
  const lastIndex = items.length - 1;
  const middle = items.slice(1, lastIndex);

  return (
    <nav
      className="relative flex min-w-0 overflow-y-visible"
      aria-label={t('breadcrumb')}
    >
      {/* The negative margin lines the first crumb's icon up with the page
          content, past the crumb's own padding. */}
      <ol
        role="list"
        className="-ml-1.5 flex min-w-0 items-center gap-0.5 overflow-x-auto py-2 text-sm"
      >
        {/* Keyed by position, not name: a crumb whose name can change while
            you are in it (an editable title) must not remount on every
            keystroke. */}
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === lastIndex;
          const isMiddle = !isFirst && !isLast;
          return (
            <li
              key={index}
              // A named last crumb may shrink, so a long current-page name uses
              // the room left and only then ends in "…". Custom `content` (e.g.
              // an editable title) keeps its own size.
              className={`${isMiddle ? 'hidden md:flex' : 'flex'} min-w-0 ${isLast && !item.content ? 'shrink' : 'shrink-0'} items-center gap-0.5`}
            >
              {!isFirst && <Separator />}
              <BreadcrumbItem {...item} isCurrent={isLast} />
              {isFirst && middle.length > 0 && (
                <span className="flex items-center gap-0.5 md:hidden">
                  <Separator />
                  <MobileBreadCrumb navigation={middle} />
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
